// Session cache for the TIRP Dashboard timeline.
//
// The dashboard needs clients + objects + inspections before it can draw a
// single bar, and those come from three separate polled endpoints. Without a
// cache, every visit starts empty and shows a cold-load flash. We stash the
// last fully-loaded snapshot in sessionStorage so re-opening the dashboard
// paints instantly, and invalidate it whenever anything the timeline shows is
// edited — the next visit then does a fresh "Loading Timeline → skeleton →
// final" load.
//
// sessionStorage (not localStorage): it's scoped to the working session, and
// we also clear it explicitly on logout, so it behaves as a true temp store.

const KEY = 'tib_timeline_cache_v1';

// Read the cached snapshot ({ clients, objects, inspections }) or null.
export function readTimelineCache() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.objects) || !Array.isArray(parsed.inspections) || !Array.isArray(parsed.clients)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// Persist the fully-loaded snapshot. Silently no-ops on quota / unavailable.
export function writeTimelineCache({ clients, objects, inspections }) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ clients, objects, inspections }));
  } catch {
    /* storage full / disabled — just skip caching */
  }
}

export function clearTimelineCache() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

// True when a write to `path` could change what the timeline displays (object
// dates/tags, inspection status/type, client names). Deliberately broad — a
// spurious clear only costs one extra fresh load, whereas a missed one would
// leave the timeline stale.
export function pathAffectsTimeline(path) {
  return /\/(objects|inspections|clients)\b/.test(path || '');
}
